import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { getContent } from "@/lib/sections";
import { PublicNav, Footer } from "@/components/Layout";

function v(x, fallback = "—") {
  return x && String(x).trim() ? x : fallback;
}

function buildMentions(salon, C) {
  const name = salon.name || "le salon";
  const editor = v(C.legal_company, name);
  const rows = [
    ["Dénomination", editor],
    C.legal_status && ["Forme juridique", C.legal_status],
    C.legal_capital && ["Capital social", C.legal_capital],
    C.legal_siret && ["SIRET", C.legal_siret],
    C.legal_vat && ["TVA intracommunautaire", C.legal_vat],
    ["Adresse", v(salon.address)],
    ["Téléphone", v(salon.phone)],
    ["Email", v(salon.email)],
    ["Directeur de la publication", v(C.legal_director, editor)],
  ].filter(Boolean);
  return {
    title: "Mentions légales",
    blocks: [
      { h: "Éditeur du site", table: rows },
      { h: "Hébergement", p: [
        `Ce site est hébergé par ${v(C.legal_host, "un prestataire d'infrastructure cloud")}.`,
        C.legal_host_details || "",
      ].filter(Boolean) },
      { h: "Propriété intellectuelle", p: [
        `L'ensemble des contenus présents sur ce site (textes, images, logo, charte graphique) est la propriété de ${editor}, sauf mention contraire. Toute reproduction, représentation ou diffusion, totale ou partielle, sans autorisation écrite préalable est interdite et constituerait une contrefaçon sanctionnée par le Code de la propriété intellectuelle.`,
      ] },
      { h: "Données personnelles", p: [
        `Le traitement des données personnelles collectées via ce site (notamment lors des réservations) est décrit dans notre Politique de confidentialité. Conformément au RGPD, vous disposez de droits d'accès, de rectification et de suppression de vos données.`,
      ] },
    ],
  };
}

function buildPrivacy(salon, C) {
  const name = salon.name || "le salon";
  const editor = v(C.legal_company, name);
  const contact = v(C.dpo_email, v(salon.email, ""));
  return {
    title: "Politique de confidentialité",
    blocks: [
      { h: "Responsable du traitement", p: [
        `${editor} est responsable du traitement des données personnelles collectées sur ce site. Pour toute question relative à vos données, vous pouvez nous contacter à ${contact || "l'adresse email indiquée dans les mentions légales"}.`,
      ] },
      { h: "Données collectées", p: [
        "Lors d'une réservation en ligne, nous collectons : votre nom, votre prénom, votre adresse email et votre numéro de téléphone. Aucune donnée bancaire n'est collectée sur ce site (le paiement s'effectue sur place).",
      ] },
      { h: "Finalités du traitement", p: [
        "Vos données sont utilisées pour : la gestion et la confirmation de vos rendez-vous, l'envoi de rappels et de notifications liés à vos rendez-vous, et la gestion de la relation client.",
      ] },
      { h: "Base légale", p: [
        "Le traitement repose sur l'exécution de mesures précontractuelles et contractuelles (la prestation que vous réservez) ainsi que, le cas échéant, sur votre consentement.",
      ] },
      { h: "Durée de conservation", p: [
        "Vos données de réservation sont conservées pendant la durée nécessaire à la gestion de la relation client, puis archivées ou supprimées conformément aux obligations légales en vigueur.",
      ] },
      { h: "Destinataires", p: [
        `Vos données sont destinées exclusivement à ${editor} et à ses sous-traitants techniques (hébergement, envoi d'emails) agissant pour son compte. Elles ne sont ni vendues, ni cédées à des tiers à des fins commerciales.`,
      ] },
      { h: "Cookies", p: [
        "Ce site utilise uniquement des cookies strictement nécessaires à son fonctionnement (gestion de session et de la réservation). Aucun cookie de publicité ou de traçage tiers n'est déposé.",
      ] },
      { h: "Vos droits", p: [
        "Conformément au Règlement général sur la protection des données (RGPD) et à la loi « Informatique et Libertés », vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de portabilité de vos données.",
        `Pour exercer ces droits, contactez-nous à ${contact || "l'adresse indiquée dans les mentions légales"}. Vous pouvez également introduire une réclamation auprès de la CNIL (www.cnil.fr).`,
      ] },
    ],
  };
}

function buildCGV(salon, C) {
  const name = salon.name || "le salon";
  const editor = v(C.legal_company, name);
  return {
    title: "Conditions générales",
    blocks: [
      { h: "Objet", p: [
        `Les présentes conditions régissent la réservation de prestations de coiffure et de barbier proposées par ${editor} via ce site internet.`,
      ] },
      { h: "Réservation", p: [
        "La réservation s'effectue en ligne en sélectionnant une prestation, un collaborateur, une date et un créneau disponible. Un email de confirmation est envoyé lorsque la configuration le permet. La réservation vaut acceptation des présentes conditions.",
      ] },
      { h: "Prix", p: [
        "Les prix des prestations sont indiqués en euros, toutes taxes comprises. Ils sont susceptibles d'évoluer ; le prix applicable est celui affiché au moment de la réservation.",
      ] },
      { h: "Paiement", p: [
        "Le règlement s'effectue directement sur place, au salon, à l'issue de la prestation, par les moyens de paiement acceptés par l'établissement.",
      ] },
      { h: "Annulation et modification", p: [
        "Vous pouvez annuler ou modifier votre rendez-vous depuis le lien de gestion reçu par email. Nous vous remercions de prévenir au plus tôt en cas d'empêchement afin de libérer le créneau pour d'autres clients.",
      ] },
      { h: "Retard", p: [
        "En cas de retard, la prestation pourra être écourtée pour respecter les rendez-vous suivants, ou reportée selon les disponibilités.",
      ] },
      { h: "Responsabilité", p: [
        `${editor} s'engage à fournir ses prestations avec soin et professionnalisme. Sa responsabilité ne saurait être engagée en cas de force majeure ou de mauvaise utilisation du service de réservation.`,
      ] },
      { h: "Droit applicable", p: [
        "Les présentes conditions sont soumises au droit français. En cas de litige, une solution amiable sera recherchée avant toute action judiciaire.",
      ] },
    ],
  };
}

const BUILDERS = { mentions: buildMentions, confidentialite: buildPrivacy, cgv: buildCGV };

export default function Legal({ doc }) {
  const { slug } = useParams();
  const [salon, setSalon] = useState(null);
  useEffect(() => {
    api.get(slug ? `/salon/${slug}` : "/salon").then((r) => setSalon(r.data?.salon || {})).catch(() => setSalon({}));
  }, [slug]);

  if (!salon) return <div className="min-h-screen bg-[#0A0908] flex items-center justify-center text-[#A39E93]">Chargement…</div>;

  const C = getContent(salon);
  const data = (BUILDERS[doc] || buildMentions)(salon, C);

  return (
    <div className="min-h-screen bg-[#0A0908] text-[#F4F0EA]">
      <PublicNav />
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-20" data-testid={`legal-${doc}`}>
        <div className="text-[10px] tracking-[0.4em] text-[#C5A059] uppercase mb-3">Informations légales</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light mb-12" data-testid="legal-title">{data.title}</h1>

        <div className="space-y-12">
          {data.blocks.map((b, i) => (
            <section key={i}>
              <h2 className="font-serif text-2xl font-light text-[#C5A059] mb-4">{b.h}</h2>
              {b.table && (
                <dl className="divide-y divide-[#2C2A26] border border-[#2C2A26]">
                  {b.table.map(([k, val], j) => (
                    <div key={j} className="grid grid-cols-3 gap-4 px-4 py-3">
                      <dt className="text-xs uppercase tracking-[0.15em] text-[#A39E93] col-span-1">{k}</dt>
                      <dd className="text-sm col-span-2 break-words">{val}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {b.p && b.p.map((para, j) => (
                <p key={j} className="text-sm text-[#A39E93] leading-relaxed mb-3">{para}</p>
              ))}
            </section>
          ))}
        </div>

        <p className="text-xs text-[#6B6760] mt-16">Dernière mise à jour : {new Date().toLocaleDateString("fr-FR")}</p>
      </main>
      <Footer />
    </div>
  );
}
